import { apiClient } from '@/lib/api/client';
import type { ProductImageIntent } from '../utils/product-image';
import type {
  MerchantImageUploadResponse,
  MerchantProductDetail,
  MerchantProductPatchInput,
  MerchantProductPatchResponse,
  MerchantProductReceiptsQueryParams,
  MerchantProductReceiptsResponse,
  MerchantProductsQueryParams,
  MerchantProductsResponse,
} from './types';

export async function getMerchantProducts(
  params: MerchantProductsQueryParams,
): Promise<MerchantProductsResponse> {
  const { data } = await apiClient.get<MerchantProductsResponse>('/merchant/products', {
    params,
  });
  return data;
}

export async function getMerchantProductDetail(productId: string): Promise<MerchantProductDetail> {
  const { data } = await apiClient.get<MerchantProductDetail>(`/merchant/products/${productId}`);
  return data;
}

export async function patchMerchantProduct(
  productId: string,
  payload: MerchantProductPatchInput,
): Promise<MerchantProductPatchResponse> {
  const { data } = await apiClient.patch<MerchantProductPatchResponse>(
    `/merchant/products/${productId}`,
    payload,
  );
  return data;
}

export async function getMerchantProductReceipts(
  params: MerchantProductReceiptsQueryParams,
): Promise<MerchantProductReceiptsResponse> {
  const { data } = await apiClient.get<MerchantProductReceiptsResponse>('/merchant/receipts', {
    params,
  });
  return data;
}

/**
 * Upload a product photo. Hand-built `FormData` so axios sets the multipart
 * boundary itself; the generated multipart body type is loose, so this posts
 * the file directly (field name `file`, per the backend contract).
 */
export async function uploadMerchantProductImage(
  file: File,
): Promise<MerchantImageUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await apiClient.post<MerchantImageUploadResponse>(
    '/merchant/uploads/image',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Apply a photo change to a product. A pasted URL patches directly; a chosen
 * file is uploaded first and its returned URL is patched; a removal patches
 * `image_url: null`. All three converge on the same PATCH so the caller only
 * invalidates once.
 */
export async function applyProductImage(
  productId: string,
  intent: ProductImageIntent,
): Promise<MerchantProductPatchResponse> {
  if (intent.kind === 'remove') {
    return patchMerchantProduct(productId, { image_url: null });
  }
  if (intent.kind === 'url') {
    return patchMerchantProduct(productId, { image_url: intent.url });
  }

  const { url } = await uploadMerchantProductImage(intent.file);
  return patchMerchantProduct(productId, { image_url: url });
}
